var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React from "react";
import { useParams } from "react-router";
import { nanoid } from "nanoid";
import { convertBitsToWeekdays, convertWeekdaysToBits, useTimetable, } from "frontend/src/domain/timetables";
import { useUpdateTimetable } from "frontend/src/domain/timetables/actions";
export var usePageMount = function () {
    var id = useParams().id;
    var timetableId = Number(id);
    var timetable = useTimetable(timetableId);
    return { timetable: timetable.data };
};
var useEditTimetableTitle = function (value) {
    var _a = __read(React.useState(value), 2), title = _a[0], setTitle = _a[1];
    var _b = __read(React.useState(null), 2), error = _b[0], setError = _b[1];
    var validate = function () {
        if (title.length === 0) {
            setError("Введите название сетки вещания.");
            return false;
        }
        return true;
    };
    return { value: title, set: setTitle, validate: validate, error: error };
};
var useEditTimetableEntries = function (initial) {
    var _a = __read(React.useState(initial.map(function (_a) {
        var title = _a.title, timeEnd = _a.timeEnd, timeStart = _a.timeStart, daysEnabled = _a.daysEnabled, rulesetId = _a.rulesetId, id = _a.id, strictOrder = _a.strictOrder, interruptPlaying = _a.interruptPlaying;
        return ({
            id: nanoid(),
            data: {
                id: id,
                title: title,
                timeEnd: timeEnd,
                rulesetId: rulesetId,
                timeStart: timeStart,
                strictOrder: strictOrder,
                interruptPlaying: interruptPlaying,
                days: convertBitsToWeekdays(daysEnabled),
            },
        });
    })), 2), entries = _a[0], setEntries = _a[1];
    var _b = __read(React.useState(entries.reduce(function (acc, entry) {
        var _a;
        return (__assign(__assign({}, acc), (_a = {}, _a[entry.id] = {
            programNotSelected: false,
            noDaysSelected: false,
            timeStartGreaterThanTimeEnd: false,
        }, _a)));
    }, {})), 2), errors = _b[0], setErrors = _b[1];
    var validate = function () {
        setErrors({});
        var validated = entries.reduce(function (acc, entry) {
            var _a;
            return __assign(__assign({}, acc), (_a = {}, _a[entry.id] = {
                noDaysSelected: entry.data.days.length === 0,
                timeStartGreaterThanTimeEnd: entry.data.timeStart > entry.data.timeEnd,
                programNotSelected: entry.data.rulesetId === -1,
            }, _a));
        }, {});
        var isValid = Object.values(validated).every(function (value) {
            return !(value.timeStartGreaterThanTimeEnd ||
                value.noDaysSelected ||
                value.programNotSelected);
        });
        if (!isValid) {
            setErrors(validated);
        }
        return isValid;
    };
    var add = function () {
        return setEntries(entries.concat({
            id: nanoid(),
            data: {
                id: -1,
                title: "",
                days: [],
                timeStart: "00:00",
                timeEnd: "23:59",
                strictOrder: false,
                interruptPlaying: false,
                rulesetId: -1,
            },
        }));
    };
    var remove = function (index) {
        return setEntries(entries.filter(function (_, idx) { return idx !== index; }));
    };
    var setTitle = function (title, index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index ? entry : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { title: title }) });
        }));
    };
    var setProgram = function (id, index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index
                ? entry
                : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { rulesetId: id }) });
        }));
    };
    var setTimeStart = function (timeStart, index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index ? entry : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { timeStart: timeStart }) });
        }));
    };
    var setTimeEnd = function (timeEnd, index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index ? entry : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { timeEnd: timeEnd }) });
        }));
    };
    var setWeekday = function (days, index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index
                ? entry
                : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { days: days }) });
        }));
    };
    var setStrictOrderPlaying = function (index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index
                ? entry
                : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { strictOrder: !entry.data.strictOrder }) });
        }));
    };
    var setInterruptPlaying = function (index) {
        return setEntries(entries.map(function (entry, idx) {
            return idx !== index
                ? entry
                : __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { interruptPlaying: !entry.data.interruptPlaying }) });
        }));
    };
    return {
        items: entries,
        add: add,
        remove: remove,
        setTitle: setTitle,
        setProgram: setProgram,
        setTimeStart: setTimeStart,
        setTimeEnd: setTimeEnd,
        setWeekday: setWeekday,
        setInterruptPlaying: setInterruptPlaying,
        setStrictOrderPlaying: setStrictOrderPlaying,
        validate: validate,
        errors: errors,
    };
};
export var useEditTimetable = function (initial) {
    var title = useEditTimetableTitle(initial.title);
    var entries = useEditTimetableEntries(initial.programs);
    var _a = useUpdateTimetable(), run = _a.run, isLoading = _a.isLoading;
    var update = function () {
        if (title.validate() && entries.validate()) {
            run({
                id: initial.id,
                title: title.value,
                entries: entries.items.map(function (item) { return (__assign(__assign({}, item.data), { strictOrder: item.data.strictOrder, interruptPlaying: item.data.interruptPlaying, daysEnabled: convertWeekdaysToBits(item.data.days) })); }),
            });
        }
    };
    return { title: title, entries: entries, update: { run: update, pending: isLoading } };
};
export var useEditTimetableForm = function (initial) {
    var timetable = useEditTimetable(initial);
    var submit = function (evt) {
        evt.preventDefault();
        timetable.update.run();
    };
    var changeTitle = function (evt) {
        return timetable.title.set(evt.target.value);
    };
    return {
        submit: submit,
        timetable: timetable,
        changeTitle: changeTitle,
    };
};
